import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useFetch } from 'use-http';

import ArrowIcon from 'assets/icons/arrow-down.svg';
import CloseIcon from 'assets/icons/close.svg';

const CustomTextField = styled(TextField)({
  '& .MuiAutocomplete-tag > svg.MuiSvgIcon-fontSizeMedium': {
    backgroundImage: `url(${CloseIcon})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    fill: 'none',
    backgroundSize: '40%',
  },
  '& button > svg.MuiSvgIcon-fontSizeMedium': {
    backgroundImage: `url(${ArrowIcon})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    fill: 'none',
    backgroundSize: '60%',
  },
});

// Componente dedicato e isolato da RHFApiAutocomplete: multi-select su
// endpoint API (geo/regions|provinces|councils) con valore persistito nello
// shape esatto atteso dal BE ({id, nome}, e-2026-06-t13), esclusione delle
// opzioni già scelte in altri campi (es. "Province (se altre)" deve
// escludere la provincia già selezionata nel campo "Provincia") e delle
// opzioni già scelte nello stesso campo. Il cascading su N genitori (es.
// "Province (se altre)" scoped sulle regioni scelte in "Regioni (se
// altre)") usa il parametro `parentIds` (CSV) supportato nativamente
// dall'endpoint — un'unica chiamata, nessun merge client-side. Non tocca
// RHFApiAutocomplete per non introdurre rischi sugli altri usi esistenti
// di quel componente.
// eslint-disable-next-line react/display-name
const RHFApiAutocompleteMultiExclude = ({
  name,
  disabled,
  endpoint,
  parentIds,
  label,
  minSearchLength,
  isRequired = false,
  rules,
  excludeIds = [],
  ...rest
}) => {
  const [currentOptions, setCurrentOptions] = useState([]);
  const { get, loading } = useFetch();
  const { control } = useFormContext();

  const hasCascade = Array.isArray(parentIds);
  const cascadeIsEmpty = hasCascade && parentIds.length === 0;

  useEffect(() => {
    if (minSearchLength === 0) {
      handleInputChange(null, '');
    }
  }, [endpoint, parentIds?.join(',')]);

  let timer;
  const handleInputChange = (event, value, selectedIds) => {
    if (cascadeIsEmpty) {
      setCurrentOptions([]);
      return;
    }
    if (value.length >= minSearchLength && endpoint) {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const parentIdsParam = hasCascade ? `&parentIds=${parentIds.join(',')}` : '';
        const queryParam = value ? `&query=${value}` : '';

        get(`${endpoint}${parentIdsParam}${queryParam}`).then(res => {
          const data =
            res?.length > 0
              ? res
                  .filter(
                    o => !excludeIds.includes(o.id) && !selectedIds?.includes(o.id)
                  )
                  .map(o => ({ id: o.id, nome: o.name || o.code }))
              : [];

          setCurrentOptions(data);
        });
      }, 500);
    }
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      defaultValue={[]}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <Autocomplete
          multiple
          options={currentOptions}
          autoComplete
          noOptionsText={'Nessun valore'}
          disabled={disabled}
          loading={loading}
          value={value}
          getOptionLabel={option => option?.nome ?? ''}
          isOptionEqualToValue={(option, val) => option.id === val.id}
          loadingText={<CircularProgress size={20} />}
          onChange={(_, newValue) => {
            onChange(newValue);
          }}
          onInputChange={(event, inputValue) =>
            handleInputChange(
              event,
              inputValue,
              value?.map(v => v.id)
            )
          }
          renderInput={params => (
            <CustomTextField
              {...params}
              label={
                <Typography variant="h4">
                  {label}
                  {isRequired && '*'}
                </Typography>
              }
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: <>{params.InputProps.endAdornment}</>,
              }}
              error={!!error}
              helperText={error?.message}
            />
          )}
          {...rest}
        />
      )}
    />
  );
};

RHFApiAutocompleteMultiExclude.propTypes = {
  name: PropTypes.string.isRequired,
  rules: PropTypes.object,
  label: PropTypes.string,
  endpoint: PropTypes.string,
  parentIds: PropTypes.array,
  disabled: PropTypes.bool,
  isRequired: PropTypes.bool,
  minSearchLength: PropTypes.number,
  excludeIds: PropTypes.array,
};

RHFApiAutocompleteMultiExclude.defaultProps = {
  disabled: false,
  minSearchLength: 3,
  excludeIds: [],
};

export { RHFApiAutocompleteMultiExclude };
